import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Delete as ClearIcon,
} from '@mui/icons-material';
import { useLogStream } from '@/hooks/useLogStream';
import LogViewer from './LogViewer';

interface LiveLogsCardProps {
  availableLevels: string[];
}

const LiveLogsCard: React.FC<LiveLogsCardProps> = ({ availableLevels }) => {
  const [streamEnabled, setStreamEnabled] = useState(false);
  const [streamMinLevel, setStreamMinLevel] = useState<string>('info');
  const [streamFilter, setStreamFilter] = useState<string>('');
  const [autoScroll, setAutoScroll] = useState(true);

  const { logs, isConnected, error: streamError, clearLogs } = useLogStream({
    enabled: streamEnabled,
    minLevel: streamMinLevel,
    filter: streamFilter || undefined,
    maxLogs: 1000,
  });

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Live Logs</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isConnected && (
              <Chip
                size="small"
                color="success"
                label="Connected"
                sx={{ mr: 1 }}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  size="small"
                />
              }
              label="Auto-scroll"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Min Level</InputLabel>
            <Select
              value={streamMinLevel}
              onChange={(e) => setStreamMinLevel(e.target.value)}
              label="Min Level"
            >
              {availableLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Filter"
            size="small"
            value={streamFilter}
            onChange={(e) => setStreamFilter(e.target.value)}
            placeholder="Regex pattern..."
            sx={{ minWidth: 200 }}
          />

          <Button
            variant={streamEnabled ? 'outlined' : 'contained'}
            color={streamEnabled ? 'error' : 'primary'}
            onClick={() => setStreamEnabled(!streamEnabled)}
            startIcon={streamEnabled ? <StopIcon /> : <PlayIcon />}
          >
            {streamEnabled ? 'Stop' : 'Start'}
          </Button>

          <Button
            variant="outlined"
            onClick={clearLogs}
            startIcon={<ClearIcon />}
            disabled={logs.length === 0}
          >
            Clear
          </Button>
        </Box>

        {streamError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {streamError}
          </Alert>
        )}

        <LogViewer logs={logs} autoScroll={autoScroll} maxHeight={400} />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {logs.length} log entries (max 1000)
        </Typography>
      </CardContent>
    </Card>
  );
};

export default LiveLogsCard;
